import { useLang } from "../../lib/i18n";
import { appCopy } from "../../lib/i18n/app";
import { IconBulb, IconFridge, IconHome, IconPlus, IconUser } from "./icon";

export type BottomNavTab = "home" | "fridge" | "bulb" | "me";

type Props = {
  active?: BottomNavTab;
  onHome?: () => void;
  onFridge?: () => void;
  onBulb?: () => void;
  onMe?: () => void;
  onAdd?: () => void;
};

const TAB_ICONS = [
  { id: "home" as const, Icon: IconHome },
  { id: "fridge" as const, Icon: IconFridge },
  { id: "bulb" as const, Icon: IconBulb },
  { id: "me" as const, Icon: IconUser },
];

export function BottomNav({ active, onHome, onFridge, onBulb, onMe, onAdd }: Props) {
  const { lang } = useLang();
  const t = appCopy[lang];
  const TAB_LABELS: Record<BottomNavTab, string> = {
    home: t.navHome,
    fridge: t.navFridge,
    bulb: t.navBulb,
    me: t.navProfile,
  };
  const TABS = TAB_ICONS.map((tab) => ({ ...tab, label: TAB_LABELS[tab.id] }));
  const handlers: Record<BottomNavTab, undefined | (() => void)> = {
    home: onHome,
    fridge: onFridge,
    bulb: onBulb,
    me: onMe,
  };
  // Center FAB is inserted between fridge (index 1) and bulb (index 2).
  return (
    <nav
      className="fixed left-3.5 right-3.5 z-30 mx-auto flex items-center justify-between px-2.5 py-2"
      style={{
        bottom: "max(24px, env(safe-area-inset-bottom))",
        maxWidth: 460,
        borderRadius: 32,
        background: "var(--nav-bg)",
        backdropFilter: "blur(16px) saturate(180%)",
        WebkitBackdropFilter: "blur(16px) saturate(180%)",
        boxShadow: "var(--shadow-nav)",
      }}
    >
      {TABS.slice(0, 2).map((tab) => (
        <TabButton key={tab.id} tab={tab} active={active === tab.id} onClick={handlers[tab.id]} />
      ))}
      <button
        type="button"
        onClick={onAdd}
        className="flex items-center justify-center transition-transform active:scale-95"
        style={{
          width: 48,
          height: 48,
          borderRadius: 24,
          background: "var(--accent)",
          color: "var(--accent-ink)",
          boxShadow: "0 6px 16px var(--accent-glow)",
        }}
        aria-label={t.addMealAria}
      >
        <IconPlus size={22} />
      </button>
      {TABS.slice(2).map((tab) => (
        <TabButton key={tab.id} tab={tab} active={active === tab.id} onClick={handlers[tab.id]} />
      ))}
    </nav>
  );
}

function TabButton({
  tab,
  active,
  onClick,
}: {
  tab: { id: BottomNavTab; Icon: typeof IconHome; label: string };
  active: boolean;
  onClick: (() => void) | undefined;
}) {
  const { Icon, label } = tab;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-1 flex flex-col items-center gap-0.5 py-1 transition-colors"
      style={{
        color: active ? "var(--ink)" : "var(--muted)",
      }}
      aria-label={label}
    >
      <span className="h-[22px] flex items-center justify-center">
        <Icon size={22} filled={active} />
      </span>
      <span className="text-[10px] font-semibold tracking-[0.02em]">{label}</span>
    </button>
  );
}
